import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import type { TableNode } from '..';
import type { NodeColumns } from '../table-store';
import { collectGroupOrderingViolations } from './group-row-ordering';

type GroupRow = Pick<
  TableNode,
  NodeColumns.id | NodeColumns.nodeType | NodeColumns.settings
>;

/**
 * Narrowing guards + dev-only invariant checks for the node-grouping data
 * model (PSTUDIO-3159). The grouping contract is convention-only — group
 * rows carry `settings.subRows`, members carry `settings.groupId` — so
 * these helpers centralize the checks that were previously scattered as
 * inline `nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G` comparisons.
 */

/**
 * True when the row is a group header. Case-insensitive on purpose: the
 * enum value is lowercase `'g'`, but `prepareRowsDataForPifCompiler`
 * upper-cases nodeType downstream and legacy/imported data may carry `'G'`
 * — a case-sensitive compare would let such a row leak past every
 * group-aware boundary (PSTUDIO-3159).
 */
export const isGroupRow = (
  row: Pick<TableNode, NodeColumns.nodeType>
): boolean =>
  String(row.nodeType).toLowerCase() === Tenancy_Node_Type_Enum_Ps_Enum.G;

/** True when the row is a member of a group (carries a parent pointer). */
export const isSubRow = (row: Pick<TableNode, NodeColumns.settings>): boolean =>
  !!row.settings?.groupId;

/**
 * Pure referential-integrity check over the whole row set. Returns one
 * human-readable string per violation; empty array = consistent. Empty
 * groups (`subRows: []`) are NOT flagged — they are legal mid-mutation and
 * auto-dissolved at the mutation exit paths.
 */
export function collectGroupInvariantViolations(
  rows: ReadonlyArray<GroupRow>
): string[] {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const violations: string[] = [];

  for (const row of rows) {
    if (isGroupRow(row)) {
      // truthy (not != null): a leftover groupId: null on a group row is legal residue, not nesting
      if (row.settings?.groupId) {
        violations.push(
          `group ${row.id} carries groupId ${row.settings.groupId} (nested groups are forbidden)`
        );
      }
      for (const subId of row.settings?.subRows ?? []) {
        const child = byId.get(subId);
        if (!child) {
          violations.push(
            `group ${row.id} subRows references missing row ${subId} (dangling id)`
          );
        } else if (isGroupRow(child)) {
          violations.push(
            `group ${row.id} subRows contains group row ${subId} (nested groups are forbidden)`
          );
        } else if (child.settings?.groupId !== row.id) {
          violations.push(
            `row ${subId} is listed in group ${row.id} subRows but its groupId is ${child.settings?.groupId ?? 'unset'}`
          );
        }
      }
    } else if (row.settings?.groupId != null) {
      const parent = byId.get(row.settings.groupId);
      if (!parent) {
        violations.push(
          `row ${row.id} groupId points at missing row ${row.settings.groupId}`
        );
      } else if (!isGroupRow(parent)) {
        violations.push(
          `row ${row.id} groupId points at non-group row ${parent.id}`
        );
      } else if (!(parent.settings?.subRows ?? []).includes(row.id)) {
        violations.push(
          `row ${row.id} groupId points at group ${parent.id} but is missing from its subRows`
        );
      }
    }
  }

  return violations;
}

/**
 * Dev-only assertion wrapper: logs (does not throw) so a violated invariant
 * never breaks production flows — it only makes drift diagnosable.
 */
export function assertGroupInvariants(
  rows: ReadonlyArray<GroupRow>,
  context: string
): void {
  if (!import.meta.env.DEV) return;
  const violations = [
    ...collectGroupInvariantViolations(rows),
    ...collectGroupOrderingViolations(rows),
  ];
  if (violations.length > 0) {
    console.error(`[group-invariants] ${context}:`, violations);
  }
}
